import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import dayjs, { type Dayjs } from "dayjs";
import EventIcon from "@mui/icons-material/Event";
import ClearIcon from "@mui/icons-material/Clear";
import {
  InputAdornment,
  Popover,
  TextField,
  Typography,
  type TextFieldProps,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import GenericCalendarPanel, {
  type CalendarPanelView,
  YEARS_PER_PAGE,
} from "@/components/dateTime/genericCalendarPanel";
import GenericTimePicker, { type GenericTimeValue } from "@/components/dateTime/genericTimePicker";
import {
  combineDateAndTime,
  formatGenericDateTime,
  parseGenericDateTime,
} from "@/components/dateTime/genericDateTimeUtils";
import dateRangeStyles from "@/components/dateTime/genericDateRange.module.css";
import styles from "@/components/dateTime/genericDateTime.module.css";
import useSegmentedDateInput from "@/components/dateTime/useSegmentedDateInput";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

interface Props {
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  onClear?: () => void;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  textFieldProps?: Omit<TextFieldProps, "value" | "onChange">;
}

const getYearsPageStart = (date: Dayjs): number =>
  Math.floor(date.year() / YEARS_PER_PAGE) * YEARS_PER_PAGE;

export default function GenericDateTime({
  value,
  onChange,
  onClear,
  disabled = false,
  clearable = true,
  className,
  textFieldProps,
}: Props) {
  const { t, i18n } = useTranslation();
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const popoverContentRef = useRef<HTMLDivElement | null>(null);
  const ignoreNextInputBlurRef = useRef(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() =>
    formatGenericDateTime(value),
  );
  const [draftDate, setDraftDate] = useState<Dayjs | null>(null);
  const [draftTime, setDraftTime] = useState<GenericTimeValue | null>(null);
  const [timeValid, setTimeValid] = useState(true);
  const [applyErrorTarget, setApplyErrorTarget] = useState<
    "date" | "time" | null
  >(null);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<
      Record<string, never>,
      "date" | "time"
    >({
      fieldOrder: [],
      focusableSelector:
        '[data-validation-focus-target], input[aria-invalid="true"], button:not([disabled])',
    });
  const [panelView, setPanelView] = useState<CalendarPanelView>("days");
  const [viewDate, setViewDate] = useState(() => new Date());
  const [yearsPageStart, setYearsPageStart] = useState(() =>
    getYearsPageStart(dayjs()),
  );
  const isDisabled = disabled || Boolean(textFieldProps?.disabled);

  useEffect(() => {
    setInputValue(formatGenericDateTime(value));
  }, [value]);

  const initializeDraft = useCallback(() => {
    const now = dayjs().second(0).millisecond(0);
    const validValue = dayjs.isDayjs(value) && value.isValid() ? value : null;
    const initialViewDate = validValue ?? now;
    const initialTime = validValue ?? now.minute(Math.floor(now.minute() / 5) * 5);

    setDraftDate(validValue);
    setDraftTime({
      hour: initialTime.hour(),
      minute: initialTime.minute(),
    });
    setTimeValid(true);
    setApplyErrorTarget(null);
    setViewDate(initialViewDate.toDate());
    setPanelView("days");
    setYearsPageStart(getYearsPageStart(initialViewDate));
  }, [value]);

  const openPopover = (event?: MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    ignoreNextInputBlurRef.current = false;
    if (isDisabled) {
      return;
    }
    initializeDraft();
    setPopoverOpen(true);
  };

  const closeAndDiscard = () => {
    setInputValue(formatGenericDateTime(value));
    setApplyErrorTarget(null);
    setPopoverOpen(false);
  };

  const handleClear = (event?: MouseEvent<HTMLElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    ignoreNextInputBlurRef.current = false;
    if (isDisabled || !clearable) {
      return;
    }
    onChange(null);
    onClear?.();
    setInputValue("");
    setPopoverOpen(false);
  };

  const handleAdornmentMouseDown = (event: MouseEvent<HTMLElement>) => {
    ignoreNextInputBlurRef.current = true;
    event.preventDefault();
  };

  const commitManualInput = () => {
    if (!inputValue.trim()) {
      if (clearable && value !== null) {
        onChange(null);
        onClear?.();
      } else if (!clearable) {
        setInputValue(formatGenericDateTime(value));
      }
      setPopoverOpen(false);
      return;
    }

    const parsed = parseGenericDateTime(inputValue);
    if (parsed) {
      const nextValue = combineDateAndTime(
        parsed,
        parsed.hour(),
        parsed.minute(),
        value,
      );
      if (!value || !nextValue.isSame(value)) {
        onChange(nextValue);
      }
      setInputValue(formatGenericDateTime(nextValue));
      setPopoverOpen(false);
      return;
    }

    setInputValue(formatGenericDateTime(value));
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const segmentedDateInput = useSegmentedDateInput({
    inputValue,
    enabled: !isDisabled,
    includeTimeSegments: true,
    externalInputRef: textFieldProps?.inputRef,
    onValueChange: (nextValue) => setInputValue(nextValue),
  });

  const handleInputKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitManualInput();
    }
  };

  const handleDateSelection = (date: Date) => {
    const selectedDate = dayjs(date);
    setDraftDate(selectedDate);
    setViewDate(selectedDate.toDate());
    setPanelView("days");
    setYearsPageStart(getYearsPageStart(selectedDate));
    setApplyErrorTarget((current) => (current === "date" ? null : current));
  };

  const handleTimeValidityChange = useCallback((isValid: boolean) => {
    setTimeValid(isValid);
    if (isValid) {
      setApplyErrorTarget((current) =>
        current === "time" ? null : current,
      );
    }
  }, []);

  const handleApply = () => {
    if (!draftDate || !draftTime || !timeValid) {
      const invalidTarget = !draftDate ? "date" : "time";
      setApplyErrorTarget(invalidTarget);
      focusFirstInvalidField({}, [invalidTarget]);
      return;
    }

    const nextValue = combineDateAndTime(
      draftDate,
      draftTime.hour,
      draftTime.minute,
      value,
    );
    onChange(nextValue);
    setInputValue(formatGenericDateTime(nextValue));
    setPopoverOpen(false);
  };

  const calendarLocale = i18n.language === "de" ? "de" : undefined;
  return (
    <div
      ref={anchorRef}
      className={`${styles.triggerField} ${className ?? ""}`.trim()}
    >
      <TextField
        {...textFieldProps}
        inputRef={segmentedDateInput.inputRef}
        fullWidth={textFieldProps?.fullWidth ?? true}
        disabled={isDisabled}
        value={inputValue}
        placeholder={t("date_time_placeholder")}
        onChange={handleInputChange}
        onClick={(event) => {
          segmentedDateInput.handleClick(event);
          textFieldProps?.onClick?.(event);
        }}
        onFocus={(event) => {
          segmentedDateInput.handleFocus(event);
          textFieldProps?.onFocus?.(event);
        }}
        onPaste={(event) => {
          segmentedDateInput.handlePaste(event);
          textFieldProps?.onPaste?.(event);
        }}
        onCut={(event) => {
          segmentedDateInput.handleCut(event);
          textFieldProps?.onCut?.(event);
        }}
        onBlur={(event) => {
          segmentedDateInput.resetActiveSegment();
          if (ignoreNextInputBlurRef.current) {
            ignoreNextInputBlurRef.current = false;
            textFieldProps?.onBlur?.(event);
            return;
          }
          const nextFocusedElement = event.relatedTarget;
          const focusMovedIntoPopover =
            nextFocusedElement instanceof Node &&
            popoverContentRef.current?.contains(nextFocusedElement);
          if (!focusMovedIntoPopover) {
            commitManualInput();
          }
          textFieldProps?.onBlur?.(event);
        }}
        onKeyDown={(event) => {
          const segmentHandled = segmentedDateInput.handleKeyDown(event);
          if (!segmentHandled) {
            handleInputKeyDown(event);
          }
          textFieldProps?.onKeyDown?.(event);
        }}
        inputProps={{
          ...textFieldProps?.inputProps,
          maxLength: 17,
        }}
        InputProps={{
          ...textFieldProps?.InputProps,
          endAdornment: (
            <InputAdornment position="end">
              {textFieldProps?.InputProps?.endAdornment}
              {clearable && Boolean(inputValue || value) && (
                <GenericButton
                  variant="icon"
                  color="iconBase"
                  aria-label={t("clear")}
                  onClick={handleClear}
                  disabled={isDisabled}
                  onMouseDown={handleAdornmentMouseDown}
                >
                  <ClearIcon />
                </GenericButton>
              )}
              <GenericButton
                variant="icon"
                color="iconBase"
                aria-label={t("datetime")}
                aria-haspopup="dialog"
                aria-expanded={popoverOpen}
                onClick={openPopover}
                disabled={isDisabled}
                onMouseDown={handleAdornmentMouseDown}
              >
                <EventIcon />
              </GenericButton>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        onClose={closeAndDiscard}
        disableAutoFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { className: styles.popoverPaper } }}
      >
        <div ref={popoverContentRef}>
          <div className={styles.pickerContent}>
            <div
              ref={registerValidationTarget("date")}
              data-validation-focus-target
              tabIndex={-1}
              role="group"
              aria-label={t("date")}
              className={`${dateRangeStyles.calendarContainer} ${styles.calendarSection}`}
            >
              <GenericCalendarPanel
                panelView={panelView}
                setPanelView={setPanelView}
                viewDate={viewDate}
                setViewDate={setViewDate}
                yearsPageStart={yearsPageStart}
                setYearsPageStart={setYearsPageStart}
                locale={calendarLocale}
                selectionMode="single"
                selected={draftDate?.toDate() ?? null}
                onSingleDateChange={handleDateSelection}
              />
              {applyErrorTarget === "date" && (
                <Typography color="error" variant="caption" role="alert">
                  {t("field_required")}
                </Typography>
              )}
            </div>
            {draftTime && (
              <GenericTimePicker
                value={draftTime}
                onChange={setDraftTime}
                onValidityChange={handleTimeValidityChange}
                validationTargetRef={registerValidationTarget("time")}
                validationMessage={
                  applyErrorTarget === "time" ? (
                    <Typography color="error" variant="caption" role="alert">
                      {t("field_required")}
                    </Typography>
                  ) : undefined
                }
              />
            )}
          </div>

          <div className={styles.actions}>
            <GenericButton
              variant="outlined"
              size="small"
              onClick={closeAndDiscard}
            >
              {t("cancel")}
            </GenericButton>
            <GenericButton
              size="small"
              onClick={handleApply}
            >
              {t("apply")}
            </GenericButton>
          </div>
        </div>
      </Popover>
    </div>
  );
}
